/**
 * Конвертирует HSL в HEX
 */
export function hslToHex(hsl: string): string {
  const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);

  if (!match) return '#000000';

  const h = parseInt(match[1], 10) / 360;
  const s = parseInt(match[2], 10) / 100;
  const l = parseInt(match[3], 10) / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 1 / 6) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 2 / 6) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 3 / 6) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 4 / 6) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 5 / 6) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Конвертирует HEX в HSL
 */
function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;

        break;

      case g:
        h = ((b - r) / d + 2) / 6;

        break;

      case b:
        h = ((r - g) / d + 4) / 6;

        break;

      default:
        break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lightness = Math.round(l * 100);

  return `hsl(${h}, ${s}%, ${lightness}%)`;
}

/**
 * Конвертирует RGB в HSL
 */
function rgbToHsl(r: number, g: number, b: number): string {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;

        break;

      case gNorm:
        h = ((bNorm - rNorm) / d + 2) / 6;

        break;

      case bNorm:
        h = ((rNorm - gNorm) / d + 4) / 6;

        break;

      default:
        break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lightness = Math.round(l * 100);

  return `hsl(${h}, ${s}%, ${lightness}%)`;
}

/**
 * Парсит цвет из строки (hex, rgb) и возвращает HSL
 */
export function parseColorString(colorStr: string): string | null {
  const trimmed = colorStr.trim();

  // RGB формат: rgb(123, 45, 67) или rgba(123, 45, 67, 0.5)
  const rgbMatch = trimmed.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s]+[\d.]+)?\)/i);

  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
      return rgbToHsl(r, g, b);
    }
  }

  // HEX формат: #123456 или #abc
  const hexMatch = trimmed.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);

  if (hexMatch) {
    let hex = hexMatch[1];

    // Расширяем короткий hex (#abc -> #aabbcc)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }

    return hexToHsl(`#${hex}`);
  }

  return null;
}

export const parseHsl = (hsl: string): { h: number; s: number; l: number } => {
  const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);

  if (!match) return { h: 0, s: 50, l: 50 };

  return {
    h: parseInt(match[1], 10),
    s: parseInt(match[2], 10),
    l: parseInt(match[3], 10),
  };
};

export const hslToString = (h: number, s: number, l: number): string => {
  return `hsl(${h}, ${s}%, ${l}%)`;
};

/**
 * Определяет, является ли цвет слишком светлым (нужна ли обводка)
 * @param color - цвет в формате HSL или HEX
 * @param threshold - порог светлоты (по умолчанию 80%)
 * @returns true, если цвет слишком светлый
 */
export function isColorTooLight(color: string, threshold: number = 80): boolean {
  if (color.startsWith('hsl')) {
    const { l } = parseHsl(color);

    return l >= threshold;
  }

  // HEX формат
  const hex = color.startsWith('#') ? color : `#${color}`;
  const hsl = hexToHsl(hex);
  const { l } = parseHsl(hsl);

  return l >= threshold;
}

/**
 * Вычисляет относительную яркость (luminance) по формуле WCAG 2.0
 * @param hex - цвет в формате HEX (#RRGGBB)
 * @returns число от 0 (черный) до 1 (белый)
 */
function getLuminance(hex: string): number {
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;

  // Расширяем короткий hex (#abc -> aabbcc)
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  const r = parseInt(fullHex.slice(0, 2), 16) / 255;
  const g = parseInt(fullHex.slice(2, 4), 16) / 255;
  const b = parseInt(fullHex.slice(4, 6), 16) / 255;

  // sRGB to linear RGB
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  const rLinear = toLinear(r);
  const gLinear = toLinear(g);
  const bLinear = toLinear(b);

  // WCAG luminance formula
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Определяет оптимальный цвет текста (белый или черный) для заданного фона
 * @param backgroundColor - цвет фона в формате HEX (#RRGGBB)
 * @returns '#ffffff' или '#000000'
 */
export function getContrastTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Порог 0.179 соответствует примерно 50% серому с учётом человеческого восприятия
  return luminance > 0.179 ? '#000000' : '#ffffff';
}
